import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { BACKEND_URL } from '../config';
import './TestAlerts.css';

function TestAlerts() {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  
  const [testData, setTestData] = useState({
    username: 'TestUser',
    months: '12',
    recipient: 'TestRecipient',
    bits: '100',
    viewers: '50',
    amount: '€5.00',
    message: 'Toller Stream!',
    tier: '1',
    level: 'Gold'
  });

  useEffect(() => {
    // Connect to socket
    socketRef.current = io(BACKEND_URL);

    socketRef.current.on('connect', () => {
      setConnected(true);
      console.log('[Test Alerts] Connected to server');
    });

    socketRef.current.on('disconnect', () => {
      setConnected(false);
      console.log('[Test Alerts] Disconnected from server');
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const sendTestAlert = (type) => {
    if (!socketRef.current || !connected) {
      setAlertMessage('❌ Keine Verbindung zum Server');
      setTimeout(() => setAlertMessage(''), 3000);
      return;
    }

    const alertData = buildAlertData(type);
    console.log(`[Test Alerts] Sending ${type}:`, alertData);
    socketRef.current.emit(`test-alert:${type}`, alertData);
    setAlertMessage(`✅ Test-Alert gesendet: ${type}`);
    setTimeout(() => setAlertMessage(''), 3000);
  };

  const buildAlertData = (type) => {
    const baseData = {
      username: testData.username,
      timestamp: new Date().toISOString()
    };

    const typeSpecificData = {
      'twitch-follow': baseData,
      
      'twitch-subscription': {
        ...baseData,
        isResub: false,
        tier: testData.tier,
        months: testData.months
      },
      
      'twitch-resub': {
        ...baseData,
        isResub: true,
        tier: testData.tier,
        months: testData.months
      },
      
      'twitch-gift-sub': {
        ...baseData,
        gifter: testData.username,
        recipient: testData.recipient,
        tier: testData.tier
      },
      
      'twitch-cheer': {
        ...baseData,
        bits: parseInt(testData.bits),
        message: testData.message
      },
      
      'twitch-raid': {
        ...baseData,
        viewers: parseInt(testData.viewers)
      },
      
      'twitch-host': {
        ...baseData,
        viewers: parseInt(testData.viewers)
      },
      
      'youtube-superchat': {
        ...baseData,
        amount: testData.amount,
        message: testData.message
      },
      
      'youtube-subscription': baseData,
      
      'youtube-membership': {
        ...baseData,
        level: testData.level
      }
    };

    return typeSpecificData[type] || baseData;
  };

  const updateField = (field, value) => {
    setTestData(prev => ({ ...prev, [field]: value }));
  };

  const alertTypes = [
    {
      category: 'Twitch Alerts',
      alerts: [
        { type: 'twitch-follow', label: 'Follow', icon: '💜', fields: ['username'] },
        { type: 'twitch-subscription', label: 'Subscription', icon: '⭐', fields: ['username', 'tier'] },
        { type: 'twitch-resub', label: 'Resub', icon: '💎', fields: ['username', 'tier', 'months'] },
        { type: 'twitch-gift-sub', label: 'Gift Sub', icon: '🎁', fields: ['username', 'recipient', 'tier'] },
        { type: 'twitch-cheer', label: 'Cheer/Bits', icon: '💰', fields: ['username', 'bits', 'message'] },
        { type: 'twitch-raid', label: 'Raid', icon: '🚀', fields: ['username', 'viewers'] },
        { type: 'twitch-host', label: 'Host', icon: '📺', fields: ['username', 'viewers'] }
      ]
    },
    {
      category: 'YouTube Alerts',
      alerts: [
        { type: 'youtube-superchat', label: 'Super Chat', icon: '💰', fields: ['username', 'amount', 'message'] },
        { type: 'youtube-subscription', label: 'Subscription', icon: '🎉', fields: ['username'] },
        { type: 'youtube-membership', label: 'Membership', icon: '👑', fields: ['username', 'level'] }
      ]
    }
  ];

  return (
    <div className="test-alerts-page">
      <div className="page-header">
        <h2>🧪 Test Alerts</h2>
        <p>Teste verschiedene Alert-Typen mit eigenen Daten</p>
        <div className="connection-status">
          {connected ? (
            <span className="status-connected">✅ Verbunden</span>
          ) : (
            <span className="status-disconnected">❌ Nicht verbunden</span>
          )}
        </div>
        {alertMessage && (
          <div className="alert-message-banner">{alertMessage}</div>
        )}
      </div>

      {/* Test Data Form */}
      <div className="test-data-form">
        <h3>📝 Test-Daten</h3>
        <div className="form-grid">
          <div className="form-field">
            <label>Username</label>
            <input
              type="text"
              value={testData.username}
              onChange={(e) => updateField('username', e.target.value)}
              placeholder="TestUser"
            />
          </div>

          <div className="form-field">
            <label>Recipient (für Gift Sub)</label>
            <input
              type="text"
              value={testData.recipient}
              onChange={(e) => updateField('recipient', e.target.value)}
              placeholder="TestRecipient"
            />
          </div>

          <div className="form-field">
            <label>Months (für Resub)</label>
            <input
              type="number"
              value={testData.months}
              onChange={(e) => updateField('months', e.target.value)}
              placeholder="12"
            />
          </div>

          <div className="form-field">
            <label>Bits</label>
            <input
              type="number"
              value={testData.bits}
              onChange={(e) => updateField('bits', e.target.value)}
              placeholder="100"
            />
          </div>

          <div className="form-field">
            <label>Viewers (für Raid/Host)</label>
            <input
              type="number"
              value={testData.viewers}
              onChange={(e) => updateField('viewers', e.target.value)}
              placeholder="50"
            />
          </div>

          <div className="form-field">
            <label>Amount (für Super Chat)</label>
            <input
              type="text"
              value={testData.amount}
              onChange={(e) => updateField('amount', e.target.value)}
              placeholder="€5.00"
            />
          </div>

          <div className="form-field">
            <label>Tier (1, 2, oder 3)</label>
            <input
              type="text"
              value={testData.tier}
              onChange={(e) => updateField('tier', e.target.value)}
              placeholder="1"
            />
          </div>

          <div className="form-field">
            <label>Level (für Membership)</label>
            <input
              type="text"
              value={testData.level}
              onChange={(e) => updateField('level', e.target.value)}
              placeholder="Gold"
            />
          </div>

          <div className="form-field full-width">
            <label>Message</label>
            <textarea
              value={testData.message}
              onChange={(e) => updateField('message', e.target.value)}
              placeholder="Toller Stream!"
              rows="2"
            />
          </div>
        </div>
      </div>

      {/* Alert Buttons */}
      {alertTypes.map(category => (
        <div key={category.category} className="alert-category">
          <h3>{category.category}</h3>
          <div className="alert-buttons-grid">
            {category.alerts.map(alertType => (
              <button
                key={alertType.type}
                className="alert-test-btn"
                onClick={() => sendTestAlert(alertType.type)}
              >
                <span className="alert-icon">{alertType.icon}</span>
                <span className="alert-label">{alertType.label}</span>
                <div className="alert-fields">
                  {alertType.fields.map(field => (
                    <span key={field} className="field-tag">{field}</span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}

      <div className="info-box">
        <h4>ℹ️ Hinweise</h4>
        <ul>
          <li>Test-Alerts werden an alle verbundenen Clients gesendet</li>
          <li>Alerts erscheinen im Chat-Overlay und in der Chat-Seite</li>
          <li>Passe die Test-Daten oben an, bevor du einen Alert sendest</li>
          <li>Die Alerts werden nicht in der Datenbank gespeichert</li>
        </ul>
      </div>
    </div>
  );
}

export default TestAlerts;
